import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, Search } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';

interface Admin {
  email: string;
  name?: string;
  groups: string[];
}

interface AssigneeSelectorProps {
  assignedTo: string;
  onChange: (email: string) => void;
  className?: string;
}

export function AssigneeSelector({ assignedTo, onChange, className = '' }: AssigneeSelectorProps) {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch admin users on mount
  useEffect(() => {
    fetchAdminUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // GET /admin/users hits Cognito for several groups and can transiently 429/5xx under load.
  // Previously any failure was swallowed and the dropdown rendered "No admins found" — which
  // looks like "this workspace has no admins" and silently blocks assignment (Playwright
  // 034-17, build 71d7bc2a). Retry a few times, then surface a real error with a Retry button.
  const fetchAdminUsers = async (attempt = 1) => {
    const MAX_ATTEMPTS = 3;
    try {
      setLoading(true);
      setLoadError(false);
      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl) throw new Error('VITE_API_URL not configured');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      // Use optimized groups filter to fetch only admin/admin_manager/super_admin users from Cognito
      const response = await fetch(`${apiUrl}/admin/users?groups=admin,admin_manager,super_admin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch admin users (HTTP ${response.status})`);
      }

      const data = await response.json();

      // Backend already filtered by groups, no need for client-side filtering
      setAdmins(data.users || []);
      setLoading(false);
    } catch (error) {
      console.error(`Error fetching admin users (attempt ${attempt}/${MAX_ATTEMPTS}):`, error);
      if (attempt < MAX_ATTEMPTS) {
        setTimeout(() => fetchAdminUsers(attempt + 1), attempt * 1000);
        return;
      }
      setLoadError(true);
      setLoading(false);
    }
  };

  const filteredAdmins = admins.filter(admin => {
    const searchLower = searchTerm.toLowerCase();
    return (
      admin.email.toLowerCase().includes(searchLower) ||
      (admin.name && admin.name.toLowerCase().includes(searchLower))
    );
  });

  const handleSelect = (email: string) => {
    onChange(email);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleRemove = () => {
    onChange('');
  };

  // Find the assigned admin's details
  const assignedAdmin = admins.find(admin => admin.email === assignedTo);

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Display assigned admin as chip OR show selector */}
      <div className="w-full">
        {assignedTo ? (
          // Show chip with assigned admin
          <div className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 flex-1">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {assignedAdmin?.name || assignedTo}
                  <button
                    onClick={handleRemove}
                    className="ml-2 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                    type="button"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              </div>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-gray-500 hover:text-gray-700 ml-2"
                type="button"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {assignedAdmin?.email && assignedAdmin.email !== assignedAdmin.name && (
              <div className="text-xs text-gray-500 mt-1 ml-1">
                {assignedAdmin.email}
              </div>
            )}
          </div>
        ) : (
          // Show search input when no assignee
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:border-gray-400 transition-colors"
            type="button"
          >
            <span className="text-gray-500 flex items-center space-x-2">
              <Search className="w-4 h-4" />
              <span>Search admins to assign...</span>
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search input inside dropdown */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to filter..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>

          {/* Admin list */}
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                Loading admins...
              </div>
            ) : loadError ? (
              <div className="px-4 py-3 text-sm text-center" data-testid="assignee-load-error">
                <div className="text-red-600">Couldn't load admins.</div>
                <button
                  type="button"
                  onClick={() => fetchAdminUsers()}
                  data-testid="assignee-retry"
                  className="mt-1 text-tdb-accent underline hover:no-underline"
                >
                  Retry
                </button>
              </div>
            ) : filteredAdmins.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No admins found
              </div>
            ) : (
              filteredAdmins.map((admin) => (
                <button
                  key={admin.email}
                  onClick={() => handleSelect(admin.email)}
                  className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors flex items-center justify-between group"
                  type="button"
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {admin.name || admin.email}
                    </div>
                    {admin.name && admin.email !== admin.name && (
                      <div className="text-xs text-gray-500">
                        {admin.email}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    {admin.groups.includes('super_admin') && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                        Super Admin
                      </span>
                    )}
                    {admin.groups.includes('admin') && !admin.groups.includes('super_admin') && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        Admin
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
